'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

/**
 * ESPN Classic Intense: Broadcast Overlay
 * Full-screen with blur backdrop for high-density modals
 */
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // ESPN Classic Intense: bg-slate-950/90 with blur backdrop
      'fixed inset-0 z-50',
      'bg-slate-950/90 backdrop-blur-xl',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * ESPN Classic Intense: Broadcast Modal
 * High-density panel with machined edges and Championship Gold accents
 */
/** True when a DialogDescription is already in the tree (including inside DialogHeader). */
function hasDialogDescription(node: React.ReactNode): boolean {
  return React.Children.toArray(node).some(child => {
    if (!React.isValidElement(child)) return false;
    const type = child.type as { displayName?: string };
    if (type?.displayName === DialogPrimitive.Description.displayName) return true;
    if (child.props && 'children' in child.props) {
      return hasDialogDescription(child.props.children as React.ReactNode);
    }
    return false;
  });
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const needsDescribedByOverride =
    !('aria-describedby' in props) && !hasDialogDescription(children);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Top-anchored at all breakpoints: vertical center (top-1/2 + -translate-y-1/2) clips tall
          // modals (e.g. league PlayerActionModal) when content height approaches viewport height.
          'fixed left-1/2 z-50 grid w-[min(100vw-1.5rem,32rem)] max-w-lg -translate-x-1/2',
          'top-[max(0.5rem,env(safe-area-inset-top,0px))]',
          'max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))]',
          'min-h-0 overflow-y-auto overscroll-contain',
          'gap-4 p-6 duration-200',
          // ESPN Classic Intense: Brushed Steel background
          'bg-gradient-to-b from-slate-800 to-slate-900',
          // Machined Edge borders
          'border-t border-l border-t-white/10 border-l-white/10',
          'border-r border-b border-r-black/40 border-b-black/40',
          // Depth shadow
          'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)]',
          'sm:rounded-lg',
          // Animations
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
          'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          className
        )}
        {...props}
        {...(needsDescribedByOverride ? { 'aria-describedby': undefined } : {})}
      >
        {children}
        <DialogPrimitive.Close
          className={cn(
            'absolute right-4 top-4 rounded-sm',
            'text-slate-400 hover:text-white',
            'ring-offset-background transition-all',
            'hover:bg-white/10',
            'focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2',
            'disabled:pointer-events-none',
            'p-1'
          )}
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * ESPN Classic Intense: Modal Header
 * Championship Gold bottom border accent
 */
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      'pb-4 border-b-2 border-[#D4AF37]/50',
      'bg-gradient-to-b from-[#D4AF37]/5 to-transparent -mx-6 -mt-6 px-6 pt-6 mb-4',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      'pt-4 border-t border-white/10 -mx-6 -mb-6 px-6 pb-6 mt-4',
      'bg-gradient-to-t from-black/20 to-transparent',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

/**
 * ESPN Classic Intense: Modal Title
 * Roboto Condensed Bold uppercase styling
 */
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-xl font-bold leading-none tracking-wide uppercase',
      'font-broadcast text-white',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-slate-400', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
